/**
 * A4 page geometry shared by the PDF chrome and the site-map planner (009).
 * Deliberately free of @react-pdf/renderer: the planner is imported eagerly
 * by ReportPreview, and react-pdf must stay out of that chunk.
 */

// A4 dimensions in points. Margins: 15 mm sides (~42.5 pt), 20 mm top/bottom (~56.7 pt).
export const PAD_H = 43
export const PAD_TOP = 56
export const PAD_BOTTOM = 44
export const HEADER_H = 28
export const FOOTER_H = 24

// Density-driven geometry (US3): page margins, block spacing and table row
// height scale with the layout's density axis. `standard` reproduces the
// legacy constants exactly.
export interface DensityGeo {
  padH: number
  padTop: number
  padBottom: number
  /** multiplier for cell/card padding */
  pad: number
  /** multiplier for vertical rhythm (margins between blocks) */
  gap: number
}

export const DENSITY_GEO: Record<'compact' | 'standard' | 'spacious', DensityGeo> = {
  compact: { padH: 36, padTop: 48, padBottom: 38, pad: 0.7, gap: 0.7 },
  standard: { padH: PAD_H, padTop: PAD_TOP, padBottom: PAD_BOTTOM, pad: 1, gap: 1 },
  spacious: { padH: 52, padTop: 66, padBottom: 52, pad: 1.35, gap: 1.35 },
}

/** A4 height in points. */
export const A4_H = 842

/** Content height of a running header/footer page at this density. */
export const pageContentHeight = (g: DensityGeo): number =>
  A4_H - (g.padTop + HEADER_H) - (g.padBottom + FOOTER_H)

/** Conservative line count for `text` at `fontSize` in `width` points.
    0.5em per glyph over-estimates both report body faces, so the estimate
    errs towards "too tall" — a block wrongly judged oversized merely loses
    its atomicity, whereas one wrongly judged to fit would corrupt the page. */
export const estimateLines = (text: string, fontSize: number, width: number): number => {
  const perLine = Math.max(1, Math.floor(width / (fontSize * 0.5)))
  return text
    .split('\n')
    .reduce((n, para) => n + Math.max(1, Math.ceil(para.trim().length / perLine)), 0)
}

// ---------------------------------------------------------------------------
// Table rows (Results Summary, Tree Schedule)
//
// Rows are atomic (wrap={false}) so a tree's row never tears across a page
// break. An atomic row taller than the page would be painted over itself, so
// a row whose estimate does not fit under the repeated header stays
// splittable instead. Mirrors tableCell/tableHeaderCell in chrome.tsx.
// ---------------------------------------------------------------------------

const TABLE_CELL_FONT = 8.5
const TABLE_HEADER_FONT = 7
const TABLE_LINE_HEIGHT = 1.3

export interface TableRowCell {
  text: string
  /** Column width as a percentage of the table, e.g. '23%' */
  width: string
}

/** Whether a table row fits one page under the repeated (two-line) header,
    and so may be atomic. `minHeight` covers non-text content (thumbnails). */
export const tableRowFitsPage = (
  cells: TableRowCell[],
  density: 'compact' | 'standard' | 'spacious',
  minHeight = 0
): boolean => {
  const g = DENSITY_GEO[density]
  const cellPad = Math.round(5 * g.pad)
  const tableWidth = 595 - 2 * g.padH
  const header = 2 * TABLE_HEADER_FONT * TABLE_LINE_HEIGHT + 2 * cellPad
  const textHeight = Math.max(
    0,
    ...cells.map(
      (c) =>
        estimateLines(
          c.text,
          TABLE_CELL_FONT,
          (parseFloat(c.width) / 100) * tableWidth - 2 * cellPad
        ) *
          TABLE_CELL_FONT *
          TABLE_LINE_HEIGHT +
        2 * cellPad
    )
  )
  return Math.max(textHeight, minHeight) <= pageContentHeight(g) - header
}
